
import React from 'react';
import { Calendar as CalendarIcon, Clock } from 'lucide-react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

const Step4ScopeDate = ({ wizardData, updateWizardData }) => {
  // If instant merge, skip this step
  if (wizardData.mergeType === 'instant') {
    return (
      <div className="text-center py-12">
        <Clock className="h-12 w-12 text-blue-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Instant Merge Selected</h3>
        <p className="text-gray-600">
          This merge will take effect immediately. No scheduling required.
        </p>
      </div>
    );
  }

  const shifts = [
    { id: 'morning', name: 'Morning Shift', type: 'pickup' },
    { id: 'evening', name: 'Evening Shift', type: 'drop' },
  ];

  const handleShiftChange = (shiftId) => {
    const selectedShift = shifts.find((s) => s.id === shiftId);
    updateWizardData('shift', selectedShift);
  };

  const handleDateSelect = (date) => {
    updateWizardData('mergeDate', date);
  };

  // Get minimum date (tomorrow)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  return (
    <div className="space-y-6">
      <div>
        <p className="text-gray-600 mb-2">
          Configure when the merge should take effect. Select the shift and date for the scheduled
          merge operation.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Shift Selection */}
        <div className="space-y-3">
          <Label htmlFor="shift" className="text-base font-medium">
            Select Shift <span className="text-red-500">*</span>
          </Label>
          <Select
            value={wizardData.shift?.id || ''}
            onValueChange={handleShiftChange}
          >
            <SelectTrigger
              id="shift"
              className="w-full h-12 text-base border-gray-300"
            >
              <SelectValue placeholder="Choose a shift" />
            </SelectTrigger>
            <SelectContent>
              {shifts.map((shift) => (
                <SelectItem key={shift.id} value={shift.id} className="text-base">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-gray-500" />
                    <div>
                      <div className="font-medium">{shift.name}</div>
                      <div className="text-xs text-gray-500 capitalize">({shift.type})</div>
                    </div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {wizardData.shift && (
            <p className="text-sm text-gray-500">
              Selected: <strong>{wizardData.shift.name}</strong>
            </p>
          )}
        </div>

        {/* Date Selection */}
        <div className="space-y-3">
          <Label htmlFor="date" className="text-base font-medium">
            Merge Date <span className="text-red-500">*</span>
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                id="date"
                variant="outline"
                className={cn(
                  'w-full h-12 justify-start text-left font-normal text-base',
                  !wizardData.mergeDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {wizardData.mergeDate ? (
                  format(wizardData.mergeDate, 'PPP')
                ) : (
                  <span>Pick a date</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={wizardData.mergeDate}
                onSelect={handleDateSelect}
                disabled={(date) => date < tomorrow}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {wizardData.mergeDate && (
            <p className="text-sm text-gray-500">
              Merge scheduled for: <strong>{format(wizardData.mergeDate, 'MMMM d, yyyy')}</strong>
            </p>
          )}
        </div>
      </div>

      {/* Summary Card */}
      {wizardData.shift && wizardData.mergeDate && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="font-semibold text-gray-900 mb-2">Schedule Summary</h4>
          <div className="space-y-1 text-sm text-gray-700">
            <p>
              <strong>Shift:</strong> {wizardData.shift.name} ({wizardData.shift.type})
            </p>
            <p>
              <strong>Date:</strong> {format(wizardData.mergeDate, 'EEEE, MMMM d, yyyy')}
            </p>
            <p className="text-blue-800 mt-2">
              The merge will be activated on the selected date for the {wizardData.shift.name.toLowerCase()}.
            </p>
          </div>
        </div>
      )}

      {/* Info Note */}
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-sm text-yellow-800">
          <strong>Note:</strong> The merge will automatically activate at the start of the selected
          shift on the specified date. All affected students and parents will receive notifications
          in advance.
        </p>
      </div>
    </div>
  );
};

export default Step4ScopeDate;
